/**
 * lighting.ts — BETA 23.19 · THE LIGHTING PASS (map plan P3, playbook §3.2)
 *
 * Dylan: "lamps in the game that actually lit illuminating. cars and things
 * on fire illuminating light that looks like its illuminating." Near-night
 * sunset mood.
 *
 * Architecture (CrossCode lightmap + OpenRCT2 LightFX + Illuminated.js — the
 * three sources converge on exactly this):
 *   1. Offscreen LIGHTMAP canvas filled with the ambient darkness color
 *      (blue-biased, never pure black — OpenRCT2's night law).
 *   2. Light pools PUNCH HOLES in the darkness with 'destination-out',
 *      drawing one PRE-BAKED radial falloff sprite (bright tight core +
 *      long soft skirt reaching exact zero — never a plain radial gradient,
 *      that reads as "a flashlight taped to the screen").
 *   3. The lightmap multiplies over the finished world (screen space).
 *   4. A second additive pass ('lighter') lays the TINTED glow on top so
 *      sodium lamps read warm and shrines read blue.
 * Perf laws (playbook §5): the falloff sprite bakes ONCE via ImageData;
 * flicker jitters alpha/scale at ~10Hz steps; zero per-frame gradient
 * allocation, zero shadowBlur.
 */

export interface LightSource {
  /** world position of the pool center (on the ground) */
  x: number;
  y: number;
  /** pool radius in wu */
  radius: number;
  /** tint 0-255 */
  r: number;
  g: number;
  b: number;
  /** 0..1 — how much darkness it punches + glow strength */
  intensity: number;
  /** 0..1 — fire-style jitter amount (0 = steady) */
  flicker?: number;
  /** true = punches darkness only, no additive glow (23.29: player pool) */
  noGlow?: boolean;
}

/** Ambient multiply color — the "almost night time sunset" grade.
 *  255,255,255 = broad daylight; lower+cooler = darker night. */
// 23.42 — retuned to Dylan's mockup grade: shadows are warm brown-olive
// (end-of-the-world earth), not cool lavender. Amber lights pop against it.
export const AMBIENT_NIGHT = { r: 148, g: 134, b: 106 };

/** Hard cap — nearest lights win (playbook: 12-light cap; we allow a few more). */
const MAX_LIGHTS = 14;

// ── baked falloff sprite ────────────────────────────────────────────────────
// OpenRCT2's curve shape: bright tight core, long soft skirt, exact zero at
// the rim. Baked once into a 256px alpha sprite.
let _falloff: HTMLCanvasElement | null = null;
function getFalloffSprite(): HTMLCanvasElement | null {
  if (_falloff) return _falloff;
  const S = 256, C = S / 2;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  const img = c2.createImageData(S, S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const d = Math.hypot(x - C + 0.5, y - C + 0.5) / C;   // 0..1..√2
      let a = 0;
      if (d < 1) {
        // core + skirt: strong inside 0.25, eased tail hitting 0 at rim
        const core = Math.max(0, 1 - d / 0.3) * 0.55;
        const skirt = Math.pow(Math.max(0, 1 - d), 1.8) * 0.75;
        a = Math.min(1, core + skirt);
      }
      img.data[(y * S + x) * 4 + 0] = 255;
      img.data[(y * S + x) * 4 + 1] = 255;
      img.data[(y * S + x) * 4 + 2] = 255;
      img.data[(y * S + x) * 4 + 3] = Math.round(a * 255);
    }
  }
  c2.putImageData(img, 0, 0);
  _falloff = cv;
  return cv;
}

// tinted glow variants for the additive pass, baked per rgb key
const _tinted = new Map<string, HTMLCanvasElement>();
function getTintedGlow(r: number, g: number, b: number): HTMLCanvasElement | null {
  const key = `${r},${g},${b}`;
  const hit = _tinted.get(key);
  if (hit) return hit;
  const base = getFalloffSprite();
  if (!base) return null;
  const cv = document.createElement('canvas');
  cv.width = base.width; cv.height = base.height;
  const c2 = cv.getContext('2d');
  if (!c2) return null;
  c2.drawImage(base, 0, 0);
  c2.globalCompositeOperation = 'source-in';
  c2.fillStyle = `rgb(${r},${g},${b})`;
  c2.fillRect(0, 0, cv.width, cv.height);
  // keep the falloff alpha: multiply the tint back over the base alpha
  c2.globalCompositeOperation = 'destination-in';
  c2.drawImage(base, 0, 0);
  if (_tinted.size > 16) _tinted.clear();
  _tinted.set(key, cv);
  return cv;
}

// ── flicker — 10Hz stepped hash noise, deterministic per light position ────
export function flickerMul(x: number, y: number, amount: number, nowMs: number): number {
  if (!amount) return 1;
  const step = Math.floor(nowMs / 100);   // 10Hz — natb19's cadence
  let h = (Math.imul(Math.round(x), 0x9E3779B1) ^ Math.imul(Math.round(y), 0x85EBCA6B) ^ Math.imul(step, 0x27D4EB2F)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xC2B2AE35) >>> 0;
  const n = (h >>> 8) / 16777216;         // 0..1
  return 1 - amount * 0.5 + n * amount;   // 1±amount/2
}

// reusable offscreen lightmap
let _lm: HTMLCanvasElement | null = null;
function _lightmap(w: number, h: number): CanvasRenderingContext2D | null {
  if (!_lm) _lm = document.createElement('canvas');
  if (_lm.width !== w || _lm.height !== h) { _lm.width = w; _lm.height = h; }
  return _lm.getContext('2d');
}

/**
 * Draw the whole lighting pass over the finished world (call in SCREEN space,
 * after world+effects, before vignettes/HUD — those are postlight).
 */
export function renderLighting(
  ctx: CanvasRenderingContext2D,
  lights: LightSource[],
  camera: { x: number; y: number; zoom: number },
  width: number,
  height: number,
  nowMs: number,
  ambient = AMBIENT_NIGHT,
): void {
  const falloff = getFalloffSprite();
  const lm = _lightmap(width, height);
  if (!falloff || !lm || !_lm) return;

  // nearest lights win the cap
  let use = lights;
  if (lights.length > MAX_LIGHTS) {
    use = [...lights].sort((a, b) =>
      (Math.hypot(a.x - camera.x, a.y - camera.y) - Math.hypot(b.x - camera.x, b.y - camera.y)),
    ).slice(0, MAX_LIGHTS);
  }

  const toX = (wx: number) => (wx - camera.x) * camera.zoom + width / 2;
  const toY = (wy: number) => (wy - camera.y) * camera.zoom + height / 2;

  // 1. ambient darkness
  lm.globalCompositeOperation = 'source-over';
  lm.globalAlpha = 1;
  lm.fillStyle = `rgb(${ambient.r},${ambient.g},${ambient.b})`;
  lm.fillRect(0, 0, width, height);

  // 2. punch the pools out of the darkness
  lm.globalCompositeOperation = 'destination-out';
  for (const L of use) {
    const f = flickerMul(L.x, L.y, L.flicker ?? 0, nowMs);
    const rPx = L.radius * camera.zoom * f;
    const sx = toX(L.x), sy = toY(L.y);
    if (sx < -rPx || sx > width + rPx || sy < -rPx || sy > height + rPx) continue;
    // ground pools are ellipses in our oblique world (0.55 — lighting reads
    // rounder than ground marks because the light volume has height)
    lm.globalAlpha = Math.min(1, L.intensity * f);
    lm.drawImage(falloff, sx - rPx, sy - rPx * 0.55, rPx * 2, rPx * 1.1);
  }
  lm.globalAlpha = 1;

  // 3. multiply the darkness over the world
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(_lm, 0, 0);

  // 4. additive tinted glow on top (draw AFTER darkness — CrossCode's order)
  ctx.globalCompositeOperation = 'lighter';
  for (const L of use) {
    if (L.noGlow) continue;
    const glow = getTintedGlow(L.r, L.g, L.b);
    if (!glow) continue;
    const f = flickerMul(L.x, L.y, L.flicker ?? 0, nowMs);
    const rPx = L.radius * camera.zoom * f;
    const sx = toX(L.x), sy = toY(L.y);
    if (sx < -rPx || sx > width + rPx || sy < -rPx || sy > height + rPx) continue;
    ctx.globalAlpha = 0.32 * L.intensity * f;
    ctx.drawImage(glow, sx - rPx, sy - rPx * 0.55, rPx * 2, rPx * 1.1);
  }
  ctx.restore();
}
